'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { BrandLogoIcon } from '@/components/ui/BrandLogo'
import { Loader2, AlertCircle } from 'lucide-react'

interface LoginBranding {
    name: string
    logoUrl?: string | null
    loginTitle?: string | null
    loginSubtitle?: string | null
    loginPrimaryColor?: string | null
    loginAccentColor?: string | null
    loginBackgroundUrl?: string | null
}

export default function LoginPage() {
    const [email, setEmail]       = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading]   = useState(false)
    const [error, setError]       = useState('')
    const [branding, setBranding] = useState<LoginBranding | null>(null)
    const router = useRouter()

    // 2FA step
    const [pending2FAToken, setPending2FAToken] = useState<string | null>(null)
    const [totpCode, setTotpCode]               = useState('')
    const [twoFALoading, setTwoFALoading]       = useState(false)

    useEffect(() => {
        let mounted = true
        fetch('/api/auth/login-branding')
            .then(r => r.ok ? r.json() : { tenant: null })
            .then(data => {
                if (!mounted) return
                const t = data.tenant || null
                setBranding(t)
                if (t) {
                    document.title = `${t.name} — HR Portal`
                    if (t.logoUrl) {
                        let link = document.querySelector<HTMLLinkElement>('link[data-tenant-favicon]')
                        if (!link) {
                            link = document.createElement('link')
                            link.rel = 'icon'
                            link.setAttribute('data-tenant-favicon', 'true')
                            document.head.appendChild(link)
                        }
                        link.href = t.logoUrl
                    }
                }
            })
            .catch(() => {})
        return () => {
            mounted = false
            document.title = 'OSTech HRMS'
            document.querySelector('link[data-tenant-favicon]')?.remove()
        }
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)
        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            })
            if (res.ok) {
                const data = await res.json()
                if (data.requires2FA) {
                    setPending2FAToken(data.pending2FAToken)
                    setLoading(false)
                    return
                }
                setTimeout(() => {
                    const role = data.user?.role
                    if (role === 'SUPER_ADMIN')     window.location.href = '/dashboard'
                    else if (role === 'ADMIN')      window.location.href = '/dashboard'
                    else if (role === 'HR')         window.location.href = '/dashboard'
                    else                            window.location.href = '/dashboard'
                }, 300)
            } else {
                const err = await res.json()
                setError(err.error || 'Login failed. Check your credentials.')
                setLoading(false)
            }
        } catch {
            setError('Connection error. Please try again.')
            setLoading(false)
        }
    }

    const handle2FASubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setTwoFALoading(true)
        try {
            const res = await fetch('/api/auth/2fa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pending2FAToken, code: totpCode })
            })
            const data = await res.json()
            if (res.ok) {
                setTimeout(() => { window.location.href = '/dashboard' }, 300)
            } else {
                setError(data.error || 'Invalid code.')
                setTwoFALoading(false)
            }
        } catch {
            setError('Connection error. Please try again.')
            setTwoFALoading(false)
        }
    }

    const primaryColor  = branding?.loginPrimaryColor  || '#020617'
    const accentColor   = branding?.loginAccentColor   || '#0072BC'
    const loginTitle    = branding?.loginTitle         || 'Welcome back'
    const loginSubtitle = branding?.loginSubtitle      || 'Sign in to your workspace'
    const companyName   = branding?.name               || 'OSTech HRMS'

    return (
        <div className="flex min-h-[100dvh] w-full overflow-hidden bg-white">

            {/* Left panel */}
            <div
                className="hidden lg:flex w-[52%] relative items-center justify-center p-16 overflow-hidden"
                style={{
                    backgroundColor: primaryColor,
                    backgroundImage: branding?.loginBackgroundUrl
                        ? `linear-gradient(rgba(2,6,23,.72),rgba(2,6,23,.72)),url(${branding.loginBackgroundUrl})`
                        : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/15 rounded-full blur-[140px] translate-x-1/4 -translate-y-1/4" />
                    <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/15 rounded-full blur-[140px] -translate-x-1/4 translate-y-1/4" />
                </div>
                <div className="absolute inset-0 opacity-[0.03]"
                    style={{ backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '40px 40px' }} />

                <div className="relative z-10 max-w-md">
                    <div className="w-16 h-16 bg-white rounded-2xl mb-10 shadow-xl flex items-center justify-center">
                        {branding?.logoUrl
                            ? <img src={branding.logoUrl} alt={companyName} className="max-w-12 max-h-12 object-contain" />
                            : <BrandLogoIcon className="w-11 h-11" />}
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-5 leading-tight">
                        {branding ? companyName : 'Manage your workforce'}<br />
                        <span style={{ color: accentColor }}>{branding ? 'HR workspace' : 'with precision.'}</span>
                    </h1>
                    <p className="text-slate-400 text-base leading-relaxed mb-12">
                        Real-time attendance, intelligent reporting, and seamless HR management — all in one platform.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {['Geofenced Attendance', 'Leave Workflows', 'Payroll Engine', 'Multi-Tenant'].map(f => (
                            <span key={f} className="px-3 py-1.5 bg-white/8 border border-white/10 rounded-full text-xs font-medium text-slate-300">{f}</span>
                        ))}
                    </div>
                    <div className="mt-12 flex items-center gap-4 pt-8 border-t border-white/10">
                        <div className="flex -space-x-3">
                            {['bg-slate-600', 'bg-slate-500', 'bg-slate-400'].map((c, i) => (
                                <div key={i} className={`w-9 h-9 rounded-full ${c} border-2 border-slate-950 flex items-center justify-center text-white text-xs font-bold`}>
                                    {String.fromCharCode(65 + i)}
                                </div>
                            ))}
                        </div>
                        <p className="text-sm text-slate-400">
                            <span className="text-white font-semibold">500+</span> companies trust OSTech HRMS
                        </p>
                    </div>
                </div>
            </div>

            {/* Right panel */}
            <div className="flex-1 flex items-center justify-center p-5 pt-20 sm:pt-5 lg:p-16 bg-white relative">

                {/* Mobile logo */}
                <div className="absolute top-6 left-6 lg:hidden flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                        {branding?.logoUrl
                            ? <img src={branding.logoUrl} alt={companyName} className="max-w-6 max-h-6 object-contain" />
                            : <BrandLogoIcon className="w-6 h-6" />}
                    </div>
                    <span className="font-bold text-slate-800 text-sm">{companyName}</span>
                </div>

                <div className="w-full max-w-sm">

                    {/* ── 2FA step ── */}
                    {pending2FAToken ? (
                        <div className="space-y-6">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">Two-Factor Auth</h2>
                                <p className="mt-1.5 text-slate-500 text-sm">Enter the 6-digit code from your authenticator app.</p>
                            </div>
                            <form onSubmit={handle2FASubmit} className="space-y-5">
                                {error && (
                                    <div className="form-error">
                                        <AlertCircle className="w-4 h-4 shrink-0" />{error}
                                    </div>
                                )}
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    required
                                    autoFocus
                                    value={totpCode}
                                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="form-input text-center text-3xl font-mono tracking-[0.4em] py-4"
                                    placeholder="000000"
                                />
                                <button type="submit" disabled={twoFALoading || totpCode.length < 6} className="btn-primary w-full py-3 text-base">
                                    {twoFALoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : 'Verify & Sign in'}
                                </button>
                            </form>
                            <button onClick={() => { setPending2FAToken(null); setTotpCode(''); setError('') }} className="w-full text-sm text-slate-400 hover:text-slate-700 transition text-center">
                                ← Back to sign in
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                {branding?.logoUrl && (
                                    <div className="hidden lg:flex mb-5 h-12 items-center">
                                        <img src={branding.logoUrl} alt={companyName} className="max-h-12 max-w-44 object-contain" />
                                    </div>
                                )}
                                <h2 className="text-2xl font-bold text-slate-900">{loginTitle}</h2>
                                <p className="mt-1.5 text-slate-500 text-sm">{loginSubtitle}</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="form-error animate-slide-up">
                                        <AlertCircle className="w-4 h-4 shrink-0" />{error}
                                    </div>
                                )}
                                <div>
                                    <label className="form-label">Email address</label>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="form-input"
                                        placeholder="you@company.com"
                                        autoComplete="email"
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label className="form-label !mb-0">Password</label>
                                        <Link href="/forgot-password" className="text-xs font-semibold transition-colors" style={{ color: accentColor }}>
                                            Forgot password?
                                        </Link>
                                    </div>
                                    <input
                                        type="password"
                                        required
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        className="form-input"
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                    />
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                    <input id="remember" type="checkbox" className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20 cursor-pointer" />
                                    <label htmlFor="remember" className="text-sm text-slate-500 cursor-pointer select-none">Remember me for 30 days</label>
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full py-3 mt-2 text-base"
                                    style={branding ? { backgroundColor: accentColor, borderColor: accentColor } : undefined}
                                >
                                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : 'Sign in to account'}
                                </button>
                            </form>

                            <p className="mt-6 text-center text-sm text-slate-500">
                                Don't have an account?{' '}
                                <Link href="#" className="font-semibold text-accent hover:text-accent-600 transition-colors">
                                    {branding ? `Contact ${companyName}` : 'Contact HR'}
                                </Link>
                            </p>
                        </>
                    )}

                </div>
            </div>
        </div>
    )
}
